import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, ExternalLink, Lock, PenLine, Star } from "lucide-react";
import { SITE, TESTIMONIALS } from "../data/site";
import { SectionHeading } from "./SectionHeading";
import { Reveal } from "./Reveal";

/* Data-driven: the moment genuine, approved client stories are added to
   TESTIMONIALS in src/data/site.js, cards render here automatically.
   Until then — an honest coming-soon state. No fabricated proof, ever. */

const STAR_GOLD = "#F5B301";
const AVATAR_COLORS = ["bg-brand-orange", "bg-brand-brown", "bg-brand-orange-dark"];

const Stars = ({ rating, size = 18 }) => (
    <span className="inline-flex items-center gap-0.5" role="img" aria-label={`${rating} out of 5 stars`}>
        {[1, 2, 3, 4, 5].map((n) => (
            <Star
                key={n}
                size={size}
                aria-hidden="true"
                style={{ color: STAR_GOLD }}
                fill={n <= Math.round(rating) ? "currentColor" : "none"}
                strokeWidth={n <= Math.round(rating) ? 0 : 1.5}
            />
        ))}
    </span>
);

const formatDate = (iso) => {
    if (!iso) return "";
    const d = new Date(`${iso}T00:00:00`);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("en-IN", { month: "long", year: "numeric" });
};

const ReviewCard = ({ t, index }) => {
    const textRef = useRef(null);
    const [expanded, setExpanded] = useState(false);
    const [clamped, setClamped] = useState(false);

    useLayoutEffect(() => {
        const el = textRef.current;
        if (!el) return undefined;
        const check = () => {
            if (!expanded) setClamped(el.scrollHeight - el.clientHeight > 2);
        };
        check();
        window.addEventListener("resize", check);
        return () => window.removeEventListener("resize", check);
    }, [expanded, t.text]);

    const initial = (t.name || "?").trim().charAt(0).toUpperCase();

    return (
        <article
            data-testid={`testimonial-card-${index}`}
            className="flex h-full flex-col rounded-2xl border border-brand-brown/10 bg-brand-paper p-6 sm:p-7"
        >
            <header className="flex items-center gap-4">
                {t.photo ? (
                    <img src={t.photo} alt="" className="h-12 w-12 shrink-0 rounded-full object-cover" />
                ) : (
                    <span
                        aria-hidden="true"
                        className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full font-display text-xl font-bold text-white ${
                            AVATAR_COLORS[index % AVATAR_COLORS.length]
                        }`}
                    >
                        {initial}
                    </span>
                )}
                <div className="min-w-0 flex-1">
                    <p className="text-base font-semibold leading-snug text-brand-brown">{t.name}</p>
                    {t.business && <p className="mt-0.5 text-xs leading-snug text-brand-muted">{t.business}</p>}
                </div>
            </header>

            <div className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-1">
                {t.rating && <Stars rating={t.rating} />}
                <span className="text-sm text-brand-muted">
                    {[formatDate(t.date), t.source ? `on ${t.source.replace(/ review$/i, "")}` : ""].filter(Boolean).join(" · ")}
                </span>
            </div>

            <p
                ref={textRef}
                className={`mt-4 text-[15px] leading-relaxed text-brand-brown/90 ${expanded ? "" : "line-clamp-6"}`}
            >
                {t.text}
            </p>
            {(clamped || expanded) && (
                <button
                    type="button"
                    onClick={() => setExpanded((v) => !v)}
                    aria-expanded={expanded}
                    className="mt-2 self-start text-sm font-semibold text-brand-orange underline decoration-brand-orange/40 underline-offset-4 transition-colors hover:text-brand-orange-dark"
                >
                    {expanded ? "Show less" : "Read more"}
                </button>
            )}
        </article>
    );
};

const ReviewsCarousel = () => {
    const trackRef = useRef(null);
    const [canPrev, setCanPrev] = useState(false);
    const [canNext, setCanNext] = useState(false);

    const reviews = TESTIMONIALS;
    const rated = reviews.filter((t) => typeof t.rating === "number");
    const average = rated.length ? rated.reduce((sum, t) => sum + t.rating, 0) / rated.length : null;
    const count = reviews.length;

    const update = useCallback(() => {
        const el = trackRef.current;
        if (!el) return;
        setCanPrev(el.scrollLeft > 4);
        setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
    }, []);

    useEffect(() => {
        update();
        const el = trackRef.current;
        if (!el) return undefined;
        el.addEventListener("scroll", update, { passive: true });
        window.addEventListener("resize", update);
        return () => {
            el.removeEventListener("scroll", update);
            window.removeEventListener("resize", update);
        };
    }, [update]);

    const scrollByCard = (dir) => {
        const el = trackRef.current;
        if (!el) return;
        const card = el.querySelector("[data-slide]");
        const gap = 20;
        const step = card ? card.getBoundingClientRect().width + gap : el.clientWidth * 0.85;
        const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        el.scrollBy({ left: dir * step, behavior: reduce ? "auto" : "smooth" });
    };

    const showArrows = canPrev || canNext;

    return (
        <Reveal delay={0.1}>
            <div
                className="mt-12 rounded-3xl border border-brand-brown/10 bg-white p-5 shadow-[0_24px_48px_-28px_rgba(53,24,13,0.18)] sm:p-8 lg:p-10"
                data-testid="reviews-panel"
            >
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <p className="font-display text-xl font-bold text-brand-brown sm:text-2xl">Google reviews</p>
                        {average !== null && (
                            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1" data-testid="reviews-summary">
                                <span className="text-2xl font-bold text-brand-brown sm:text-3xl">{average.toFixed(1)}</span>
                                <Stars rating={average} size={20} />
                                <span className="text-sm text-brand-muted sm:text-base">
                                    {count} {count === 1 ? "review" : "reviews"}
                                </span>
                            </div>
                        )}
                    </div>
                    {showArrows && (
                        <div className="flex shrink-0 gap-2">
                            <button
                                type="button"
                                onClick={() => scrollByCard(-1)}
                                disabled={!canPrev}
                                aria-label="Previous review"
                                data-testid="reviews-prev"
                                className="flex h-11 w-11 items-center justify-center rounded-full border border-brand-brown/15 bg-white text-brand-brown transition-colors hover:border-brand-orange hover:text-brand-orange disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:border-brand-brown/15 disabled:hover:text-brand-brown sm:h-12 sm:w-12"
                            >
                                <ChevronLeft size={20} aria-hidden="true" />
                            </button>
                            <button
                                type="button"
                                onClick={() => scrollByCard(1)}
                                disabled={!canNext}
                                aria-label="Next review"
                                data-testid="reviews-next"
                                className="flex h-11 w-11 items-center justify-center rounded-full border border-brand-brown/15 bg-white text-brand-brown transition-colors hover:border-brand-orange hover:text-brand-orange disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:border-brand-brown/15 disabled:hover:text-brand-brown sm:h-12 sm:w-12"
                            >
                                <ChevronRight size={20} aria-hidden="true" />
                            </button>
                        </div>
                    )}
                </div>

                <div
                    ref={trackRef}
                    role="region"
                    aria-roledescription="carousel"
                    aria-label="Client reviews"
                    tabIndex={0}
                    data-testid="reviews-track"
                    className="-mx-5 mt-7 flex snap-x snap-mandatory gap-5 overflow-x-auto scroll-px-5 px-5 pb-2 [scrollbar-width:none] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange/40 sm:-mx-8 sm:scroll-px-8 sm:px-8 lg:-mx-10 lg:scroll-px-10 lg:px-10 [&::-webkit-scrollbar]:hidden"
                >
                    {reviews.map((t, i) => (
                        <div
                            key={`${t.name}-${i}`}
                            data-slide
                            className={`w-[86%] shrink-0 snap-start sm:w-[62%] md:w-[calc(50%-10px)] ${
                                reviews.length > 2 ? "lg:w-[calc(33.333%-14px)]" : ""
                            }`}
                        >
                            <ReviewCard t={t} index={i} />
                        </div>
                    ))}
                </div>

                {(SITE.googleWriteReviewUrl || SITE.googleReviewsUrl) && (
                    <div className="mt-8 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center">
                        {SITE.googleWriteReviewUrl && (
                            <a
                                href={SITE.googleWriteReviewUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="reviews-write-link"
                                className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-orange px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-brand-orange-dark sm:text-base"
                            >
                                <PenLine size={16} aria-hidden="true" />
                                Leave us a review
                            </a>
                        )}
                        {SITE.googleReviewsUrl && (
                            <a
                                href={SITE.googleReviewsUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="reviews-google-link"
                                className="inline-flex items-center justify-center gap-2 rounded-full border border-brand-brown/15 bg-white px-6 py-3 text-sm font-semibold text-brand-brown transition-colors hover:border-brand-orange hover:text-brand-orange sm:text-base"
                            >
                                Read all reviews on Google
                                <ExternalLink size={16} aria-hidden="true" />
                            </a>
                        )}
                    </div>
                )}
            </div>
        </Reveal>
    );
};

export const Results = () => (
    <section id="results" className="py-16 sm:py-24 lg:py-32" data-testid="results-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
            <SectionHeading
                chapter="07"
                kicker="Results"
                title="Client results & stories."
                testid="results-heading"
            />
            {TESTIMONIALS.length === 0 ? (
                <Reveal delay={0.1}>
                    <div
                        className="mt-12 rounded-2xl border-2 border-dashed border-brand-brown/20 bg-brand-cream/60 p-10 text-center sm:p-16"
                        data-testid="results-coming-soon"
                    >
                        <span className="inline-flex items-center gap-2 rounded-full border border-brand-orange/40 bg-brand-orange-tint px-4 py-1.5 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-brand-orange">
                            <Lock size={12} aria-hidden="true" />
                            In verification
                        </span>
                        <h3 className="mt-6 font-display text-2xl font-bold text-brand-brown sm:text-3xl">
                            Client results &amp; stories — coming soon.
                        </h3>
                        <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-brand-muted sm:text-base">
                            We're a new studio, and we won't fake it. No invented testimonials, no borrowed logos, no made-up
                            numbers. The moment real client stories are ready — with real names, real businesses and figures
                            they've approved — they'll live right here.
                        </p>
                        <p className="mt-6 font-mono text-xs uppercase tracking-[0.25em] text-brand-brown/50">
                            No invented proof. Ever.
                        </p>
                    </div>
                </Reveal>
            ) : (
                <ReviewsCarousel />
            )}
        </div>
    </section>
);
